import { useCallback, useMemo, useState } from 'react'
import { useOpen } from '../../../search-result-listing/header-section/searchbar/hooks'
import { useTranslation } from '../../../../hooks/locale'
import EventRegionsAutocompleteSuggestionTransport from '../../../../transports/event/event-regions-autocomplete-suggestion-transport'
import { BaseSearchbarHistoryItem } from '../../../../redux/searchbar/types'
import { useDatePickerType } from '../../../common/date-picker/hooks'
import { getLocationPickerLabel } from './utils'
import { getDatePickerLabel } from '../../../search-result-listing/header-section/searchbar/utils'

export const useSearchbar = ({
    initialArrival,
    initialDeparture,
    initialLocation,
    handleSubmit,
}: {
    initialArrival: Date | null
    initialDeparture: Date | null
    initialLocation: string
    handleSubmit: (location: string, arrivalDate: Date | null, departureDate: Date | null) => void
}) => {
    const initialArrivalDate = useMemo(() => initialArrival ?? null, [initialArrival])
    const initialDepartureDate = useMemo(() => initialDeparture ?? null, [initialDeparture])

    const [searchButtonVisible, setSearchButtonVisible] = useState<boolean>(false)

    const [location, setLocation] = useState(initialLocation)

    const [arrivalDate, setArrivalDate] = useState(initialArrivalDate)
    const [departureDate, setDepartureDate] = useState(initialDepartureDate)

    const [isAllInputsModalOpen, setAllInputsModalOpen] = useState(false)

    const {
        isOpen: isLocationPickerOpen,
        onOpen: onLocationPickerOpen,
        onClose: onLocationPickerClose,
    } = useOpen(false)

    const { isOpen: isDatePickerOpen, onOpen: onDatePickerOpen, onClose: onDatePickerClose } = useOpen(false)

    const { t } = useTranslation()

    const toggleDatePicker = useCallback(() => {
        if (isDatePickerOpen) {
            onDatePickerClose()
        } else {
            onDatePickerOpen()
        }
    }, [isDatePickerOpen, onDatePickerClose, onDatePickerOpen])

    const _resetDates = useCallback(() => {
        setArrivalDate(initialArrivalDate)
        setDepartureDate(initialDepartureDate)
    }, [initialArrivalDate, initialDepartureDate])

    const _resetAllInputs = useCallback(() => {
        _resetDates()
    }, [_resetDates])

    const _handleAllInputsModalOpen = useCallback(() => {
        setAllInputsModalOpen(true)
    }, [])

    const _handleAllInputsModalClose = useCallback(() => {
        _resetAllInputs()
        setAllInputsModalOpen(false)
    }, [_resetAllInputs])

    const _handleLocationInputChange = useCallback((input: string) => {
        setLocation(input)
    }, [])

    const _handleLocationInputSelect = useCallback(
        (input: string) => {
            _handleLocationInputChange(input)
            onLocationPickerClose()
            if (!arrivalDate || !departureDate) {
                onDatePickerOpen()
            }
        },
        [_handleLocationInputChange, arrivalDate, departureDate, onDatePickerOpen, onLocationPickerClose],
    )

    const _handleLocationItemSelect = useCallback(
        (item: EventRegionsAutocompleteSuggestionTransport) => {
            setLocation(item)
            if (!arrivalDate || !departureDate) {
                onDatePickerOpen()
            }
        },
        [arrivalDate, departureDate, onDatePickerOpen],
    )

    const _handleHistoryItemSelect = useCallback((historyItem: BaseSearchbarHistoryItem) => {
        const { search, arrival, departure } = historyItem
        setLocation(search)
        setArrivalDate(arrival)
        setDepartureDate(departure)
    }, [])

    const _handleSubmit = useCallback(() => {
        setAllInputsModalOpen(false)
        onDatePickerClose()
        onLocationPickerClose()
        handleSubmit(location, arrivalDate, departureDate)
    }, [onDatePickerClose, onLocationPickerClose, location, arrivalDate, departureDate, handleSubmit])

    const datePickerType = useDatePickerType({ endDate: departureDate })

    const _locationPickerLabel = getLocationPickerLabel(location, t('whereTo'))
    const _datePickerLabel = useMemo(
        () =>
            getDatePickerLabel({
                startDate: arrivalDate,
                endDate: departureDate,
                startDatePlaceholder: t('checkIn'),
                endDatePlaceholder: t('checkOut'),
                datePickerType,
            }),
        [arrivalDate, departureDate, t, datePickerType],
    )

    const showSearchbar = useCallback(() => setSearchButtonVisible(true), [])

    return {
        location,
        arrivalDate,
        departureDate,
        setArrivalDate,
        setDepartureDate,
        searchButtonVisible,
        isAllInputsModalOpen,
        isLocationPickerOpen,
        onLocationPickerOpen,
        onLocationPickerClose,
        isDatePickerOpen,
        onDatePickerOpen,
        onDatePickerClose,
        toggleDatePicker,
        handleLocationInputChange: _handleLocationInputChange,
        handleAllInputsModalOpen: _handleAllInputsModalOpen,
        handleAllInputsModalClose: _handleAllInputsModalClose,
        handleLocationInputSelect: _handleLocationInputSelect,
        handleLocationItemSelect: _handleLocationItemSelect,
        handleHistoryItemSelect: _handleHistoryItemSelect,
        handleSubmit: _handleSubmit,
        locationPickerLabel: _locationPickerLabel,
        datePickerLabel: _datePickerLabel,
        showSearchbar: showSearchbar,
    }
}
